import { FC } from "react";
import { ConfirmDialog, makeSxStyles } from "ui";
import { AutomatedRouteNotificationsModal } from "./automated-route-notifications-modal";
import { Modals } from "../../../state/route-planner-store";
import {
  AdminGetDateRouteNotificationsResDto,
  AdminGetRoutesResDto,
} from "service/api/estabrookSchemas";
import { useRoutePlannerNotificationsApi } from "../hooks/use-route-planner-notifications-api";
import { DateTime } from "luxon";

interface RoutePlannerNotificationModalsProps {
  activeModal: Modals | null;
  routes?: AdminGetRoutesResDto[];
  pendingNotificationUpdate:
    | {
        routeId: string;
        routeName: string;
      }
    | null
    | undefined;
  globalNotificationSettings?: AdminGetDateRouteNotificationsResDto;
  selectedDate: DateTime;
  onClose: () => void;
}

export const RoutePlannerNotificationModals: FC<
  RoutePlannerNotificationModalsProps
> = ({
  activeModal,
  routes,
  pendingNotificationUpdate,
  globalNotificationSettings,
  selectedDate,
  onClose,
}) => {
  const styles = useStyles();

  const {
    handleMuteAllNotificationsRoute,
    handleEnableAllNotificationsRoute,
    handleMuteAllNotificationsGlobal,
    handleEnableAllNotificationsGlobal,
    handleNotificationSettingsSave,
    handleGlobalNotificationSettingsSave,
  } = useRoutePlannerNotificationsApi({
    selectedDate,
    pendingNotificationUpdate,
    closeModal: onClose,
  });

  return (
    <>
      {/* Route-specific notification modals */}
      <ConfirmDialog
        title="Mute All Notifications"
        description="Are you sure you want to mute all notifications for this route?"
        cancelText="Cancel"
        confirmText="Mute All"
        open={activeModal === Modals.MUTE_ALL_NOTIFICATIONS_ROUTE}
        onConfirm={handleMuteAllNotificationsRoute}
        onClose={onClose}
      />

      <ConfirmDialog
        title="Enable All Notifications"
        description="Are you sure you want to enable all notifications for this route?"
        cancelText="Cancel"
        confirmText="Enable All"
        open={activeModal === Modals.SEND_ALL_NOTIFICATIONS_ROUTE}
        onConfirm={handleEnableAllNotificationsRoute}
        onClose={onClose}
      />

      {/* Global notification modals */}
      <ConfirmDialog
        title="Mute All Notifications"
        description="Are you sure you want to mute all notifications for all routes?"
        subtext="No notifications will be sent to customer for any route."
        cancelText="Cancel"
        confirmText="Mute All"
        open={activeModal === Modals.MUTE_ALL_NOTIFICATIONS_GLOBAL}
        onConfirm={handleMuteAllNotificationsGlobal}
        onClose={onClose}
      />

      <ConfirmDialog
        title="Enable All Notifications"
        description="Are you sure you want to enable all notifications for all routes?"
        subtext="Enabling all notifications will allow notifications to be sent globally. This does not override individual route preferences—notifications will still follow each route's specific settings."
        cancelText="Cancel"
        confirmText="Enable All"
        open={activeModal === Modals.SEND_ALL_NOTIFICATIONS_GLOBAL}
        onConfirm={handleEnableAllNotificationsGlobal}
        onClose={onClose}
      />

      {/* Customize notifications modal */}
      <AutomatedRouteNotificationsModal
        open={activeModal === Modals.CUSTOMIZE_NOTIFICATIONS}
        onClose={onClose}
        description="Enable customized plow notifications for this route"
        currentSettings={
          pendingNotificationUpdate
            ? routes?.find((r) => r.id === pendingNotificationUpdate.routeId)
                ?.notificationSettings
            : globalNotificationSettings
        }
        globalNotificationSettings={globalNotificationSettings}
        onSave={handleNotificationSettingsSave}
        sx={styles.driverUpdateDialogWidth}
      />
      <AutomatedRouteNotificationsModal
        open={activeModal === Modals.CUSTOMIZE_GLOBAL_NOTIFICATIONS}
        isGlobal={true}
        description="Enabling customized plow notifications for all routes will allow selected notifications to be sent globally. This does not override individual route preferences—notifications will still follow each route's specific settings."
        onClose={onClose}
        currentSettings={globalNotificationSettings}
        globalNotificationSettings={globalNotificationSettings}
        onSave={handleGlobalNotificationSettingsSave}
        sx={styles.driverUpdateDialogWidth}
      />
    </>
  );
};

const useStyles = makeSxStyles(() => ({
  driverUpdateDialogWidth: {
    "& .MuiPaper-root": {
      minWidth: "400px",
    },
  },
}));
